"use client";

import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import type { Theme } from "@/config/themes";
import { AniOsBrandingBackground } from "@/components/branding/AniOsBrandingLayer";
import { syncResolvedBrandingCache, buildStickyWatermarkScope } from "@/components/branding/brandingCacheSync";
import {
  OsProvider,
  useOsWindowList,
  useOsActions,
  useOsSettings,
  useOsFocus,
  useWindowById,
  useOsWindows,
  useOsWindowListOptional,
  useOsActionsOptional,
  useOsSettingsOptional,
} from "./OsContext";
import { OsWorkspaceBar } from "./OsWorkspaceBar";
import { OsDesktop } from "./OsDesktop";
import { OsDock } from "./OsDock";
import { OsAniSidebar } from "./OsAniSidebar";
import {
  OS_ANI_DOCK_GUTTER_CLASS,
} from "./osWindowChrome";
import { useAni } from "@/context/AniContext";
import { useAuth } from "@/context/AuthContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { useInAppAgentProfile } from "@/hooks/useInAppAgentProfile";
import { resolveAndOpenDefaultLandingWindow } from "./osDefaultLanding";
import { AnimatedEllipsis } from "@/components/ui/AnimatedEllipsis";
import { parseUrlToWindowConfig, buildWindowUrl } from "./urlMapping";
import { APP_REGISTRY } from "./OsAppRegistry";
import { findWindowShowingUrl } from "./windowKey";
import { useOpenUrl } from "./openUrl";
import { getOsDockSafeAreaBottomPx } from "./osDockSafeArea";
import { useGlobalFeTools } from "@/hooks/useGlobalFeTools";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { GlobalSearchDialog } from "@/components/global-search/GlobalSearchDialog";
import { useOsShortcuts } from "./useOsShortcuts";
import type { AgentWindowParams } from "./osAgentWindowParams";
import type { OsAniSnapshotSource } from "./buildOsAniContextSnapshot";
import { parseJsonValue, type JsonValue } from "@/components/guardians/jsonValue";
import { GuardiansAlertNotificationListener } from "@/components/guardians/GuardiansAlertNotificationListener";
import { OpenworkOsDemoLayer } from "@/components/review/OpenworkOsDemoLayer";
import { ReviewQueueInAppAgentContextLayer } from "@/components/review/ReviewQueueInAppAgentContextLayer";
import { WorkspaceWelcomeLayer } from "@/components/workspace/WorkspaceWelcomeLayer";
import { useIsDarkMode } from "@/utils/colorScheme";
import { readOpenWindowsMaximized } from "@/utils/osUserSettingsStorage";
import { useOsUserSettings } from "@/hooks/useOsUserSettings";

function toValidatedWindowParams(
  params: AgentWindowParams,
): Record<string, JsonValue> {
  const out: Record<string, JsonValue> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      out[key] = value;
      continue;
    }
    if (typeof value === "object") {
      const parsed = parseJsonValue(JSON.stringify(value));
      if (parsed !== null) {
        out[key] = parsed;
      }
    }
  }
  return out;
}

function stringifyWindowParams(
  params?: Record<string, JsonValue>,
): Record<string, string> {
  if (!params) return {};

  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [
        key,
        typeof value === "string" ? value : JSON.stringify(value),
      ]),
  );
}

/**
 * OsDeepLinkHandler - Handles opening windows from URL deep links on page load.
 * Renders nothing, just handles the side effect.
 */

function OsDeepLinkHandler({ onInitialUrlHandled }: { onInitialUrlHandled: () => void }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { user } = useAuth();
  const { permissions, hasPermission, isLoading: isPermissionsLoading } =
    useModulePermissions();
  const { getWindowsSnapshot, activeWorkspaceId } = useOsWindowList();
  const { isWorkspaceReady } = useOsSettings();
  const { openWindow, focusWindow } = useOsActions();
  const openUrl = useOpenUrl();
  const lastHandledLocationKeyRef = useRef<string | null>(null);
  const hasHandledIdleLandingRef = useRef(false);

  useEffect(() => {
    if (!isWorkspaceReady || isPermissionsLoading || !permissions) {
      return;
    }
    onInitialUrlHandled();

    // History entries keep the sync marker, so only skip the replace itself — back/forward must still apply.
    const locationState = location.state as { osUrlSync?: boolean } | null;
    if (navigationType === "REPLACE" && locationState?.osUrlSync) {
      return;
    }

    const pathname = location.pathname;
    const search = location.search;
    const windows = getWindowsSnapshot();

    const isDefaultLanding =
      pathname === "/" ||
      pathname === "" ||
      pathname === "/home" ||
      pathname === "/dashboard";

    if (isDefaultLanding) {
      if (!hasHandledIdleLandingRef.current) {
        const persistedActive = windows.filter((w) => !w.isMinimized);
        if (persistedActive.length > 0) {
          const top = persistedActive.reduce((a, b) =>
            b.zIndex > a.zIndex ? b : a,
          );
          focusWindow(top.id);
        } else {
          resolveAndOpenDefaultLandingWindow({
            companyId: user?.company_id,
            initialRoute: permissions.initial_route,
            hasPermission,
            windows,
            openWindow,
            focusWindow,
          });
        }
        hasHandledIdleLandingRef.current = true;
      }
      return;
    }

    if (lastHandledLocationKeyRef.current === location.key) {
      return;
    }
    lastHandledLocationKeyRef.current = location.key;

    const url = pathname + search;
    const windowShowingUrl = findWindowShowingUrl(windows, url, activeWorkspaceId);
    if (windowShowingUrl) {
      focusWindow(windowShowingUrl.id);
      return;
    }

    openUrl(url, { replaceParams: true });
  }, [
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigationType,
    isWorkspaceReady,
    getWindowsSnapshot,
    openWindow,
    focusWindow,
    openUrl,
    activeWorkspaceId,
    hasPermission,
    permissions,
    isPermissionsLoading,
    user?.company_id,
    onInitialUrlHandled,
  ]);

  return null;
}

/**
 * OsUrlSyncHandler - Syncs the browser URL to reflect the currently focused window.
 * Uses replaceState to avoid polluting browser history with every focus change.
 * Renders nothing, just handles the side effect.
 */
const OS_IDLE_PATHS = new Set(["/", "/home", "/dashboard"]);

/** `enabled` stays false until OsDeepLinkHandler has read the initial URL, so restored windows cannot overwrite it. */
function OsUrlSyncHandler({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate();
  const { focusedWindowKey, focusedWindowId } = useOsFocus();
  const { isWorkspaceReady } = useOsSettings();
  const focusedWindow = useWindowById(focusedWindowId ?? "");
  const focusedSerializedUrl = focusedWindow
    ? buildWindowUrl(focusedWindow.appId, focusedWindow.params)
    : null;
  const prevSyncedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isWorkspaceReady || !enabled) {
      return;
    }

    const browserPath = window.location.pathname;
    const browserSearch = window.location.search;
    const browserSerialized = browserPath + browserSearch;
    const pendingDeepLink = parseUrlToWindowConfig(browserPath, browserSearch);

    if (!focusedWindowKey || !focusedWindowId || !focusedWindow || !focusedSerializedUrl) {
      if (!OS_IDLE_PATHS.has(browserPath)) {
        // Let OsDeepLinkHandler open the window before forcing /home — unless this URL was synced from a window that has since closed.
        if (pendingDeepLink && prevSyncedUrlRef.current !== browserSerialized) {
          return;
        }
        const idlePath = "/home";
        if (prevSyncedUrlRef.current !== idlePath) {
          prevSyncedUrlRef.current = idlePath;
          navigate(idlePath, { replace: true, state: { osUrlSync: true } });
        }
      } else {
        prevSyncedUrlRef.current = null;
      }
      return;
    }

    const newPath = focusedSerializedUrl;
    if (prevSyncedUrlRef.current === newPath) {
      return;
    }
    prevSyncedUrlRef.current = newPath;

    if (newPath !== browserSerialized) {
      navigate(newPath, { replace: true, state: { osUrlSync: true } });
    }
  }, [
    focusedWindowKey,
    focusedWindowId,
    focusedSerializedUrl,
    focusedWindow,
    isWorkspaceReady,
    enabled,
    navigate,
  ]);

  return null;
}

const DOCUMENT_TITLE_SUFFIX = "Ani Tech";

/**
 * Keeps the browser tab title aligned with the focused OS window, not the
 * catch-all route meta when URL sync points at OS-only paths.
 */
function OsDocumentTitleSync() {
  const { focusedWindowKey, focusedWindowId } = useOsFocus();
  const { getWindow } = useOsWindowList();

  useEffect(() => {
    if (!focusedWindowKey || !focusedWindowId) {
      const path = window.location.pathname;
      if (path === "/home" || path === "/") {
        document.title = DOCUMENT_TITLE_SUFFIX;
      }
      return;
    }

    const focusedWindow = getWindow(focusedWindowId);
    const title = focusedWindow?.title?.trim();
    document.title = title
      ? `${title} | ${DOCUMENT_TITLE_SUFFIX}`
      : DOCUMENT_TITLE_SUFFIX;
  }, [focusedWindowKey, focusedWindowId, getWindow]);

  return null;
}

/**
 * Skips one render during React Fast Refresh when OsProvider is not mounted yet.
 * Prevents layout hooks from throwing in that edge frame.
 */
function OsLayoutGate({
  availableThemes,
  onSelectTheme,
  onToggleLayout,
  workspaceBanner,
}: {
  availableThemes?: Theme[];
  onSelectTheme?: (theme: Theme) => void;
  onToggleLayout?: () => void;
  workspaceBanner?: React.ReactNode;
}) {
  const windowList = useOsWindowListOptional();
  const actions = useOsActionsOptional();
  const settings = useOsSettingsOptional();
  if (!windowList || !actions || !settings) {
    return (
      <div
        data-testid="os-layout-gate-bootstrap"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0f172a]"
      >
        <div className="text-slate-300 text-lg font-medium">
          Preparing workspaces<AnimatedEllipsis />
        </div>
      </div>
    );
  }
  return (
    <OsLayoutInner
      availableThemes={availableThemes}
      onSelectTheme={onSelectTheme}
      onToggleLayout={onToggleLayout}
      workspaceBanner={workspaceBanner}
    />
  );
}

/**
 * OsLayoutInner - The inner layout that uses workspace themes
 */
function OsLayoutInner({
  availableThemes,
  onSelectTheme: parentOnSelectTheme,
  onToggleLayout,
  workspaceBanner,
}: {
  availableThemes?: Theme[];
  onSelectTheme?: (theme: Theme) => void;
  onToggleLayout?: () => void;
  workspaceBanner?: React.ReactNode;
}) {
  const location = useLocation();
  const [isInitialUrlHandled, setIsInitialUrlHandled] = useState(false);
  const markInitialUrlHandled = useCallback(() => setIsInitialUrlHandled(true), []);
  const { workspaces, activeWorkspaceId, getWindow, getWindowsSnapshot, windowIdsByWorkspace } = useOsWindowList();
  const { getFocusedWindow } = useOsWindows();
  const { hasMaximizedWindowInActiveWorkspace } = useOsFocus();
  const {
    updateWorkspaceTheme,
    adjustWindowsForDesktopResize,
    clampWindowsToDesktopBounds,
    openWindow,
    closeWindow,
    focusWindow,
    updateWindow,
  } = useOsActions();
  const {
    isLoading,
    isWorkspaceReady,
    setAvailableThemes,
  } = useOsSettings();
  const { widgetViewState, setWidgetViewState } = useAni();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { hasPermission } = useModulePermissions();
  // Users without the "ani" module permission cannot access the Ani assistant
  // (dock button hidden, sidebar not mounted).
  const canUseAni = hasPermission("ani");
  useInAppAgentProfile(canUseAni ? user?.company_id : undefined);

  const userId = user?.id;
  const { openWindowsMaximized: openWindowsMaximizedPref } = useOsUserSettings();
  const defaultOpenWindowsMaximized =
    openWindowsMaximizedPref || readOpenWindowsMaximized(userId);
  const userCompanyId = user?.company_id;
  useEffect(() => {
    const scope = buildStickyWatermarkScope({ id: userId, company_id: userCompanyId });
    if (!scope) return;
    void syncResolvedBrandingCache(queryClient, { scope });
  }, [queryClient, userCompanyId, userId]);

  // All hooks must be called before any conditional returns (Rules of Hooks)
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [isBackgroundFading, setIsBackgroundFading] = useState(false);
  // Don't initialize displayedTheme until we have the actual workspace theme to prevent flash
  const [displayedTheme, setDisplayedTheme] = useState<Theme | undefined>(undefined);
  const [isInitialThemeSet, setIsInitialThemeSet] = useState(false);
  const prevSidebarStateRef = useRef<boolean>(false);
  const prevDockedWidthRef = useRef<number>(widgetViewState.dockedWidth);
  const prevDesktopWidthRef = useRef<number>(0);
  const openWindowForAgent = useCallback(
    (config: {
      appId: string;
      title?: string;
      params?: AgentWindowParams;
    }) => {
      let demoteWindowId: string | undefined;
      if (
        config.appId === "task-review" &&
        !defaultOpenWindowsMaximized &&
        APP_REGISTRY[config.appId]?.openWindowed !== true
      ) {
        const ids = windowIdsByWorkspace.get(activeWorkspaceId) ?? [];
        for (const id of ids) {
          const window = getWindow(id);
          if (window?.isMaximized) {
            demoteWindowId = window.id;
            break;
          }
        }
      }

      const stringParams = config.params
        ? stringifyWindowParams(toValidatedWindowParams(config.params))
        : {};

      openWindow({
        appId: config.appId,
        title: config.title || APP_REGISTRY[config.appId]?.label || config.appId,
        params: stringParams,
        demoteWindowId,
      });
    },
    [activeWorkspaceId, defaultOpenWindowsMaximized, getWindow, openWindow, windowIdsByWorkspace],
  );

  const readAgentSnapshot = useCallback(
    (): OsAniSnapshotSource => ({
      workspaces,
      activeWorkspaceId,
      windowIdsByWorkspace,
      getWindow,
      getWindowsSnapshot,
      getFocusedWindow,
      focusedAppIdInActiveWorkspace: getFocusedWindow()?.appId ?? null,
    }),
    [activeWorkspaceId, getFocusedWindow, getWindow, getWindowsSnapshot, windowIdsByWorkspace, workspaces],
  );

  const { isSearchOpen, initialQuery, openSearch, closeSearch } = useGlobalSearch();

  // Register global FE tools for in-app agent
  useGlobalFeTools({
    openWindow: openWindowForAgent,
    closeWindow,
    focusWindow,
    updateWindow,
    openSearch,
    readSnapshot: readAgentSnapshot,
  });

  // Get current workspace theme
  const activeWorkspace = useMemo(() => {
    return workspaces.find((ws) => ws.id === activeWorkspaceId);
  }, [workspaces, activeWorkspaceId]);
  const workspaceThemeSeed = activeWorkspace?.themeSeed;

  // Find theme based on workspace theme seed
  const currentTheme = useMemo(() => {
    if (!workspaceThemeSeed || !availableThemes) return availableThemes?.[0];
    return availableThemes.find((t) => t.seed === workspaceThemeSeed) || availableThemes[0];
  }, [workspaceThemeSeed, availableThemes]);

  // Handle theme selection for current workspace
  const _handleSelectTheme = useCallback((theme: Theme) => {
    if (activeWorkspace && theme.seed) {
      updateWorkspaceTheme(activeWorkspace.id, theme.seed);
    }
    parentOnSelectTheme?.(theme);
  }, [activeWorkspace, updateWorkspaceTheme, parentOnSelectTheme]);

  const handleAniDockClick = useCallback(() => {
    setWidgetViewState({ 
      isOpen: !widgetViewState.isOpen
    });
  }, [widgetViewState.isOpen, setWidgetViewState]);

  // Update available themes in context when they change
  useEffect(() => {
    if (availableThemes) {
      setAvailableThemes(availableThemes);
    }
  }, [availableThemes, setAvailableThemes]);

  // Adjust windows when sidebar opens/closes (only for docked mode)
  useEffect(() => {
    // Skip effects while loading
    if (isLoading) return;

    const isOpen = widgetViewState.isOpen;
    const isFloating = widgetViewState.mode === "floating";
    const shouldPushContent = isOpen && !isFloating;
    const mainContent = mainContentRef.current;
    
    // Initialize previous width on first render
    if (mainContent && prevDesktopWidthRef.current === 0) {
      prevDesktopWidthRef.current = mainContent.clientWidth;
    }
    
    const dockedWidthChanged =
      shouldPushContent && prevDockedWidthRef.current !== widgetViewState.dockedWidth;
    const sidebarOpenStateChanged = prevSidebarStateRef.current !== shouldPushContent;

    // Adjust when sidebar opens/closes or docked width commits (docked mode only)
    if ((sidebarOpenStateChanged || dockedWidthChanged) && !isFloating && mainContent) {
      // Delay to allow sidebar animation to complete fully (300ms animation + 50ms buffer)
      setTimeout(() => {
        const oldWidth = prevDesktopWidthRef.current;
        const newWidth = mainContent.clientWidth;

        if (oldWidth !== newWidth) {
          adjustWindowsForDesktopResize(newWidth, oldWidth);
          prevDesktopWidthRef.current = newWidth;
        }
      }, 350);
    }

    prevSidebarStateRef.current = shouldPushContent;
    prevDockedWidthRef.current = widgetViewState.dockedWidth;
  }, [
    isLoading,
    widgetViewState.isOpen,
    widgetViewState.mode,
    widgetViewState.dockedWidth,
    adjustWindowsForDesktopResize,
  ]);

  // Keep windows inside #main-content when its size changes (viewport, toolbar, dock, sidebar)
  // Also reserves the dock safe-area when the dock is pinned visible so maximised
  // windows do not sit underneath it.
  useEffect(() => {
    if (isLoading) return;
    const el = mainContentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const CLAMP_DEBOUNCE_MS = 100;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let rafId: number | null = null;

    const runClamp = () => {
      const rootFs = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const reserveDockSpace = !hasMaximizedWindowInActiveWorkspace;
      const dockBottomPx = getOsDockSafeAreaBottomPx(
        Number.isFinite(rootFs) ? rootFs : 16,
        { reserveDockSpace },
      );
      const fullHeight = el.clientHeight;
      const windowedHeight = Math.max(0, fullHeight - dockBottomPx);
      clampWindowsToDesktopBounds(el.clientWidth, windowedHeight, fullHeight);
    };

    const scheduleClamp = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          debounceTimer = null;
          runClamp();
        }, CLAMP_DEBOUNCE_MS);
      });
    };

    runClamp();
    const ro = new ResizeObserver(scheduleClamp);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [isLoading, clampWindowsToDesktopBounds, hasMaximizedWindowInActiveWorkspace]);

  // Handle theme transitions with fade effect
  useEffect(() => {
    if (currentTheme && currentTheme.seed !== displayedTheme?.seed) {
      // For initial theme set (when displayedTheme is undefined), skip the fade animation
      // This prevents the flash of a different theme color on load
      if (!isInitialThemeSet) {
        setDisplayedTheme(currentTheme);
        setIsInitialThemeSet(true);
        return;
      }

      // For subsequent theme changes, use fade transition
      setIsBackgroundFading(true);
      
      // After fade out animation completes, switch theme
      const timer = setTimeout(() => {
        setDisplayedTheme(currentTheme);
        setIsBackgroundFading(false);
      }, 250);
      
      return () => clearTimeout(timer);
    }
  }, [currentTheme, displayedTheme, isInitialThemeSet]);

  const toggleAniAssistant = useCallback(() => {
    if (!canUseAni) {
      return;
    }
    setWidgetViewState({
      isOpen: !widgetViewState.isOpen,
    });
  }, [canUseAni, setWidgetViewState, widgetViewState.isOpen]);

  useOsShortcuts({
    isSearchOpen,
    openSearch,
    closeSearch,
    toggleAniAssistant,
  });

  const isDarkMode = useIsDarkMode();
  const isAniOpen = widgetViewState.isOpen;
  const isAniDocked = widgetViewState.mode === "docked";
  const shouldPushContent = isAniOpen && isAniDocked;
  const dockCollapsed = hasMaximizedWindowInActiveWorkspace;

  const deepLinkConfig = useMemo(
    () => parseUrlToWindowConfig(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const showBootstrapOverlay = isLoading && !deepLinkConfig;

  return (
    <div
      data-testid="os-layout"
      className={`relative isolate flex w-full h-full overflow-hidden overscroll-none ${
        shouldPushContent
          ? OS_ANI_DOCK_GUTTER_CLASS
          : "bg-transparent"
      }`}
    >
      {showBootstrapOverlay ? (
        <div
          data-testid="os-layout-loading"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0f172a]"
        >
          <div className="text-slate-300 text-lg font-medium">
            Preparing workspaces<AnimatedEllipsis />
          </div>
        </div>
      ) : null}

      <div
        className={
          showBootstrapOverlay
            ? "invisible pointer-events-none flex min-h-0 min-w-0 flex-1 flex-col"
            : "contents"
        }
        aria-hidden={showBootstrapOverlay}
      >
      {/* Handle deep link URL on initial load */}
      <OsDeepLinkHandler onInitialUrlHandled={markInitialUrlHandled} />
      {/* Alert toasts need OsProvider so View opens/focuses the Guardians window */}
      <GuardiansAlertNotificationListener />
      {/* Sync URL to reflect focused window */}
      <OsUrlSyncHandler enabled={isInitialUrlHandled} />
      <OsDocumentTitleSync />

      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <AniOsBrandingBackground
          displayedTheme={displayedTheme}
          isDarkMode={isDarkMode}
          isBackgroundFading={isBackgroundFading}
        />
      </div>

      {/* Main OS Interface Column — toolbar in flow; dock overlays bottom when auto-hide */}
      <div className="relative z-10 flex min-w-0 flex-1 flex-col overflow-hidden overscroll-none">
        {/* Workspace toolbar — always visible */}
        <div className="relative z-30 shrink-0">
          <OsWorkspaceBar
            onToggleLayout={onToggleLayout}
          />
        </div>

        {workspaceBanner ? (
          <div className="relative z-30 shrink-0">{workspaceBanner}</div>
        ) : null}

        {/* Desktop; dock overlays bottom edge */}
        <main className="relative z-10 flex min-h-0 flex-1 flex-col">
          <div
            id="main-content"
            ref={mainContentRef}
            data-testid="os-layout-content"
            className="relative z-[1] min-h-0 w-full flex-1 overflow-hidden overscroll-none"
          >
            <OsDesktop />

            <div className="pointer-events-none absolute inset-0 z-[10050]">
              <WorkspaceWelcomeLayer />
              <OpenworkOsDemoLayer />
              <ReviewQueueInAppAgentContextLayer />
            </div>

            {isWorkspaceReady && (
              <div
                className="pointer-events-none absolute bottom-0 left-0 right-0 z-[9999] flex items-end justify-center"
                data-testid="os-dock-overlay"
              >
                <div
                  className={`w-fit transition-transform duration-300 ease-out ${
                    dockCollapsed ? "pointer-events-none" : "pointer-events-auto"
                  }`}
                  style={{
                    transform: dockCollapsed ? "translateY(100%)" : "translateY(0)",
                  }}
                  data-testid="os-dock-slide"
                >
                  <OsDock
                    onAniClick={canUseAni ? handleAniDockClick : undefined}
                    isAniOpen={isAniOpen}
                    openSearch={openSearch}
                  />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Ani Sidebar - sits alongside the entire OS interface */}
      {canUseAni && <OsAniSidebar />}

      <GlobalSearchDialog isOpen={isSearchOpen} initialQuery={initialQuery} onClose={closeSearch} />
      </div>
    </div>
  );
}

/**
 * OsLayout — the primary application shell, replacing SidebarLayout.
 *
 * Provides a full-height OS-style layout with workspace-based themes.
 *
 * @param availableThemes - Available themes for selection
 * @param onSelectTheme - Callback when a theme is selected
 */
export function OsLayout({
  navbar: _navbar,
  sidebar: _sidebar,
  openIcon: _openIcon,
  closeIcon: _closeIcon,
  hasColumns: _hasColumns = false,
  currentTheme: _currentTheme,
  availableThemes,
  onSelectTheme,
  onToggleLayout,
  workspaceBanner,
}: {
  navbar?: React.ReactNode;
  sidebar?: React.ReactNode;
  openIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
  hasColumns?: boolean;
  companyId?: string;
  currentTheme?: Theme;
  availableThemes?: Theme[];
  onSelectTheme?: (theme: Theme) => void;
  onToggleLayout?: () => void;
  workspaceBanner?: React.ReactNode;
}) {
  return (
    <OsProvider>
      {/* Honour the OS "reduce motion" setting for all framer-motion shell
          animations (dock, sidebar) without changing default behaviour. */}
      <MotionConfig reducedMotion="user">
        <OsLayoutGate
          availableThemes={availableThemes}
          onSelectTheme={onSelectTheme}
          onToggleLayout={onToggleLayout}
          workspaceBanner={workspaceBanner}
        />
      </MotionConfig>
    </OsProvider>
  );
}
